import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { concat, forkJoin, Observable, of } from 'rxjs';
import {
    catchError,
    map,
    mapTo,
    mergeMap,
    switchMap,
    tap,
} from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import { NextCoachingService } from '../../coaching-base/coaching.service';
import {
    CommitmentEntity,
    InteractionEntity,
    InteractionEntityType,
    NoteEntity,
    NoteEntityType,
    ObservationEntity,
} from '../../coaching-base/config';
import { DraftCommitmentService } from '../../coaching-base/draft-commitment.service';
import {
    CommitmentApprovalDto,
    CommitmentDraftDto,
    CommitmentDto,
    CommitmentEvent,
    CommitmentObservationLinkFocusPostDto,
    EventParticipantDto,
    LookupBox,
} from '../../coaching-base/general';
import { ObservationService } from '../../coaching-observation/observation/observation.service';
import { BehaviourItem } from '../../coaching-tags/models/behaviour.coaching.model';
import { MetricItem } from '../../coaching-tags/models/metric.coaching.model';
import { SubBehaviourItem } from '../../coaching-tags/models/subbehaviour.coaching.model';
import { composeTagIdentifierNext } from '../../coaching-tags/tags/tags.model';
import { Dependencies } from '../../store';
import { pluck } from '../../store.decorator';
import { PayloadAction } from '../../store.model';
import { createCommitment } from '../create-commitment.function';
import { CommitmentService } from '../services/commitment.service';

import {
    ActionType,
    RootDeleteAction,
    rootDeleteResult,
    RootPublishAction,
    rootPublishResult,
    RootReloadAction,
    rootReloadResult,
    RootRetrieveObservationSummaryAction,
    rootRetrieveObservationSummaryResult,
    RootSaveAction,
    rootSaveResult,
    RootSendApprovalAction,
    RootSignOffApprovalAction,
    rootSignOffApprovalResult,
    sendApprovalResult,
} from './commitment.action';
import { commitmentFormValueReverseTransformer } from './commitment.form';
import { RootState, TagInfo } from './commitment.state';

export interface ChainGroup<TPayload> {
    root: TPayload;

    // lookups
    behaviours?: BehaviourItem[];
    subBehaviours?: SubBehaviourItem[];
    metrics?: MetricItem[];

    // config
    coachingEvent?: CommitmentEntity;
    noteEntity?: NoteEntity;
    interactionEntity?: InteractionEntity;

    // main
    draftId?: string;
    draft?: CommitmentDraftDto;
    approval?: CommitmentApprovalDto;
    commitment?: CommitmentDto;
    performer?: EventParticipantDto;
    coach?: EventParticipantDto;
    event?: CommitmentEvent;
    tagInfo?: TagInfo;
}

function chainActions(
    chainableActions: ActionType[],
    actions: Array<PayloadAction<any, any>>,
    storePath: string,
): Array<Observable<PayloadAction<any, any>>> {
    const result: Array<Observable<PayloadAction<any, any>>> = [];
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    actions &&
        actions.forEach((e) => {
            if (chainableActions.includes(e.type)) {
                e.storePath = storePath;
                result.push(of(e));
            }
        });
    return result;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootReload = (
    action: ActionsObservable<PayloadAction<ActionType, RootReloadAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_COMMITMENT__ROOT__RELOAD),
        switchMap((act) => {
            const coachingService =
                deps.injector.get<NextCoachingService>(NextCoachingService);
            const draftCommitmentService =
                deps.injector.get<DraftCommitmentService>(
                    DraftCommitmentService,
                );
            const observationService =
                deps.injector.get<ObservationService>(ObservationService);
            const commitmentService =
                deps.injector.get<CommitmentService>(CommitmentService);
            const grp: ChainGroup<RootReloadAction> = {
                root: act.payload,
            };

            const obses: [
                Observable<LookupBox>,
                Observable<CommitmentEntity>,
                Observable<ObservationEntity>,
                Observable<
                    CommitmentDraftDto | CommitmentApprovalDto | CommitmentDto
                >,
                Observable<CommitmentDraftDto>,
            ] = [
                of(null).pipe(
                    coachingService.retrieveLookupBox(),
                    tap((ret) => {
                        grp.behaviours = ret.behaviours;
                        grp.subBehaviours = ret.subBehaviours;
                        grp.metrics = ret.metrics;
                    }),
                ),
                of(null).pipe(
                    commitmentService.retrieveCommitmentConfig(), // TODO post-refactor. redux check if already loaded
                    // TODO consider consolidating
                    tap((ret) => {
                        // TODO revise vet and throw?
                        grp.coachingEvent = ret;
                        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                        grp.noteEntity = ret.childEntities.find(
                            (e) => e.entityType === NoteEntityType,
                        ) as NoteEntity;
                    }),
                ),
                of(null).pipe(
                    observationService.retrieveObservationConfig(),
                    tap((ret) => {
                        grp.interactionEntity = ret.childEntities.find(
                            (a) => a.entityType === InteractionEntityType,
                        ) as InteractionEntity;
                    }),
                ),
                of(null), // existing draft or commitment
                of(null), // new commitment
            ];

            const approvalIdPresent =
                act.payload.approvalId != null &&
                !isNaN(act.payload.approvalId);
            if (act.payload.draftId && !approvalIdPresent) {
                obses[3] = of(null).pipe(
                    draftCommitmentService.retrieve(() => ({
                        draftId: act.payload.draftId,
                    })),
                    tap<CommitmentDraftDto>((ret) => {
                        grp.draftId = ret.draftId;
                        grp.draft = ret;
                        grp.commitment = ret.commitment;
                    }),
                );
            } else if (act.payload.draftId && approvalIdPresent) {
                // TODO revise. this block might become redundant. see usages of CommitmentDraftDto.Approval
                obses[3] = of(null).pipe(
                    draftCommitmentService.retrieveApproval(() => ({
                        approvalId: act.payload.approvalId,
                    })),
                    tap<CommitmentApprovalDto>((ret) => {
                        // don't set grp.draftId since it's the approval that's the primary and not the regular draft
                        grp.approval = ret;
                        grp.commitment = ret.commitment;
                    }),
                );
            } else if (
                act.payload.commitmentId != null &&
                !isNaN(act.payload.commitmentId)
            ) {
                obses[3] = of(null).pipe(
                    commitmentService.retrieve(() => ({
                        commitmentId: act.payload.commitmentId,
                    })),
                    // eslint-disable-next-line no-return-assign
                    tap<CommitmentDto>((ret) => (grp.commitment = ret)),
                    switchMap((ret) => {
                        const approvalObs = of(ret);
                        if (!act.payload.loadLastApprovalIfItWasDone) {
                            return approvalObs;
                        }
                        const lastApprovalIsClosed =
                            ret.draftAndApproval &&
                            !ret.draftAndApproval.isOriginalCommitment &&
                            ret.draftAndApproval.approvalId != null &&
                            ret.draftAndApproval.approved != null;
                        if (!lastApprovalIsClosed) {
                            return approvalObs;
                        }
                        return approvalObs.pipe(
                            draftCommitmentService.retrieveApproval(() => ({
                                approvalId: ret.draftAndApproval.approvalId,
                            })),
                            // eslint-disable-next-line no-return-assign
                            tap((ret2) => (grp.approval = ret2)),
                            mapTo(ret),
                        );
                    }),
                );
            }

            /* always call this for the moment in order to get the participants and to-be-linked-observations.
            this is because migrated commitments from the old coaching model sometimes don't have the expected.
            the retrieve() and retrieveApproval() apis may be patched in a later phase */
            obses[4] = of(null).pipe(
                draftCommitmentService.newDraft(() => {
                    const linkFocuses: CommitmentObservationLinkFocusPostDto[] =
                        [];
                    if (
                        act.payload.observationId != null &&
                        !isNaN(act.payload.observationId)
                    ) {
                        linkFocuses.push({
                            observationId: act.payload.observationId,
                            behaviourId: act.payload.focus.behaviourId,
                            subBehaviourId: act.payload.focus.subBehaviourId,
                        });
                    }

                    return {
                        params: {
                            performerId: act.payload.performerId,
                            approvalId: act.payload.approvalId,
                            focus: {
                                focusId: null,
                                userId: null,
                                behaviourId: act.payload.focus.behaviourId,
                                subBehaviourId:
                                    act.payload.focus.subBehaviourId,
                                createdBy: null,
                                createdOn: null,
                            },
                            linkFocuses,
                        },
                    };
                }),
            );

            return forkJoin(obses).pipe(
                tap((ret) => {
                    // i.e. when grp.commitment equals null
                    if (ret[3] == null) {
                        grp.commitment = ret[4].commitment;
                    } else {
                        const obsExisted = grp.commitment.observations.some(
                            (e) =>
                                e.event.eventId === act.payload.observationId,
                        );
                        if (!obsExisted && ret[4].commitment.observations) {
                            grp.commitment.observations.push(
                                ...ret[4].commitment.observations,
                            );
                        }

                        // override the participants
                        grp.commitment.latestEventParticipants =
                            ret[4].commitment.latestEventParticipants;
                    }
                }),
                map(() => {
                    return rootReloadResult(
                        {
                            ...act.payload,
                            behaviours: grp.behaviours,
                            subBehaviours: grp.subBehaviours,
                            metrics: grp.metrics,
                            coachingEvent: grp.coachingEvent,
                            noteEntity: grp.noteEntity,
                            interactionEntity: grp.interactionEntity,
                            draftId: grp.draftId,
                            draft: grp.draft,
                            approval: grp.approval,
                            commitment: grp.commitment,
                            goalsCollection: {
                                commitmentKpis: grp.commitment.goals,
                            },
                        },
                        act.storePath,
                    );
                }),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootReloadResult(
                            {
                                ...act.payload,
                                commitment: createCommitment(null),
                            },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootSave = (
    action: ActionsObservable<PayloadAction<ActionType, RootSaveAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_COMMITMENT__ROOT__SAVE),
        switchMap((act) => {
            const rootState = pluck<RootState>(state.value, act.storePath);
            const authService = deps.injector.get<AuthService>(AuthService);
            const draftCommitmentService =
                deps.injector.get<DraftCommitmentService>(
                    DraftCommitmentService,
                );
            const grp: ChainGroup<RootSaveAction> = {
                root: act.payload,
                behaviours: rootState.behaviours,
                subBehaviours: rootState.subBehaviours,
                draftId: rootState.draftId,
                commitment: rootState.commitment,
                event: rootState.event,
                performer: rootState.performer,
                coach: rootState.coach,
            };

            const commitment = commitmentFormValueReverseTransformer(
                act.payload.formValue,
                grp.commitment,
                grp.event,
                [grp.performer, grp.coach],
                authService.user.id,
                act.payload.isGeneric,
                act.payload.activeMetricId,
            );

            /// /////////////////

            let obs = of(null);
            if (!grp.draftId) {
                obs = obs.pipe(
                    draftCommitmentService.create(() => ({
                        commitment,
                    })),
                    tap((ret) => {
                        grp.draftId = ret.draftId;
                        grp.draft = ret;
                        grp.commitment = ret.commitment;
                    }),
                );
            } else {
                obs = obs.pipe(
                    draftCommitmentService.update(() => ({
                        draftId: grp.draftId,
                        commitment,
                    })),
                    tap((ret) => {
                        grp.draft = ret;
                        grp.commitment = ret.commitment;
                    }),
                );
            }

            /// /////////////////

            return obs.pipe(
                mergeMap(() => {
                    const nextActObs = of(
                        rootSaveResult(
                            {
                                ...act.payload,
                                draftId: grp.draftId,
                                draft: grp.draft,
                                commitment: grp.commitment,
                                goalsCollection: {
                                    commitmentKpis: grp.commitment.goals,
                                },
                            },
                            act.storePath,
                        ),
                    );
                    const otherObs = chainActions(
                        [
                            ActionType.COACHING_COMMITMENT__ROOT__PUBLISH,
                            ActionType.COACHING_COMMITMENT__ROOT__CLOSE,
                        ],
                        act.payload.actions,
                        act.storePath,
                    );
                    return concat(nextActObs, ...otherObs);
                }),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootSaveResult(
                            {
                                ...act.payload,
                                commitment: createCommitment(null),
                            },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootRetrieveCommitmentDiff = (
    action: ActionsObservable<PayloadAction<ActionType, RootPublishAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_COMMITMENT__ROOT__PUBLISH),
        mergeMap((act) => {
            const rootState = pluck<RootState>(state.value, act.storePath);
            const draftCommitmentService =
                deps.injector.get<DraftCommitmentService>(
                    DraftCommitmentService,
                );

            let obs = of(null);

            if (act.payload.open) {
                obs = obs.pipe(
                    draftCommitmentService.diff(() => ({
                        draftId: rootState.draftId,
                    })),
                );
            }

            return obs.pipe(
                map((ret) =>
                    rootPublishResult(
                        {
                            ...act.payload,
                            diff: ret,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootPublishResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootDelete = (
    action: ActionsObservable<PayloadAction<ActionType, RootDeleteAction>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_COMMITMENT__ROOT__DELETE),
        switchMap((act) => {
            const draftCommitmentService =
                deps.injector.get<DraftCommitmentService>(
                    DraftCommitmentService,
                );
            const commitmentService =
                deps.injector.get<CommitmentService>(CommitmentService);

            let obs = of(null);

            // only one or the other. drafts are higher than the commitment itself
            if (act.payload.draftId) {
                obs = obs.pipe(
                    draftCommitmentService.delete(() => act.payload.draftId),
                );
            } else if (act.payload.commitmentId) {
                obs = obs.pipe(
                    commitmentService.delete(() => act.payload.commitmentId),
                );
            }

            return obs.pipe(
                mergeMap((ret) => {
                    const nextActObs = of(
                        rootDeleteResult({ success: ret }, act.storePath),
                    );
                    return concat(nextActObs);
                }),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootDeleteResult({ success: false }, act.storePath),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootRetrieveObservationSummary = (
    action: ActionsObservable<
        PayloadAction<ActionType, RootRetrieveObservationSummaryAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(
            ActionType.COACHING_COMMITMENT__ROOT__RETRIEVE_OBSERVATION_SUMMARY,
        ),
        mergeMap((act) => {
            const rootState = pluck<RootState>(state.value, act.storePath);
            const observationService =
                deps.injector.get<ObservationService>(ObservationService);
            const grp: ChainGroup<RootRetrieveObservationSummaryAction> = {
                root: act.payload,
                tagInfo: rootState.tagInfo,
            };

            // TODO api should do a better job of handling undefined/null subBehaviour
            const focusStr = composeTagIdentifierNext(
                grp.tagInfo.focus,
            ).replace(/-(undefined|null)/, '');
            return of(null).pipe(
                observationService.retrieveSummary(() => ({
                    observationId: act.payload.observationId,
                    focusStrs: [focusStr],
                })),
                map((ret) =>
                    rootRetrieveObservationSummaryResult(
                        {
                            ...act.payload,
                            observation: ret,
                        },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        rootRetrieveObservationSummaryResult(
                            { ...act.payload },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootSendApproval = (
    action: ActionsObservable<
        PayloadAction<ActionType, RootSendApprovalAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_COMMITMENT__ROOT__SEND_APPROVAL),
        mergeMap((act) => {
            const rootState = pluck<RootState>(state.value, act.storePath);
            const draftCommitmentService =
                deps.injector.get<DraftCommitmentService>(
                    DraftCommitmentService,
                );

            return of(null).pipe(
                draftCommitmentService.send(() => ({
                    draftId: rootState.draftId,
                    approvalPostDto: {
                        toUserId: act.payload.toUserId,
                        nextStatusId: act.payload.nextStatus,
                        approved: act.payload.approved,
                        closeReasonId: act.payload.closeReasonId,
                        closeReasonName: act.payload.closeReasonName,
                        timeZoneOffset: new Date().getTimezoneOffset(),
                    },
                })),
                mergeMap((ret) => {
                    const nextActObs = of(
                        sendApprovalResult(
                            {
                                ...act.payload,
                                approval: ret,
                                goalsCollection: {
                                    commitmentKpis: ret.commitment.goals,
                                },
                            },
                            act.storePath,
                        ),
                    );
                    return concat(nextActObs);
                }),
                catchError((err) => {
                    console.log(err);
                    return of(null);
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const rootSignOffApproval = (
    action: ActionsObservable<
        PayloadAction<ActionType, RootSignOffApprovalAction>
    >,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.COACHING_COMMITMENT__ROOT__SIGN_OFF_APPROVAL),
        mergeMap((act) => {
            const rootState = pluck<RootState>(state.value, act.storePath);
            const draftCommitmentService =
                deps.injector.get<DraftCommitmentService>(
                    DraftCommitmentService,
                );

            return of(null).pipe(
                draftCommitmentService.signOff(() => ({
                    approvalId: rootState.approval.approvalId,
                    approvalPostDto: {
                        nextStatusId: act.payload.nextStatus,
                        approved: act.payload.approved,
                        items: act.payload.items,
                        timeZoneOffset: new Date().getTimezoneOffset(),
                    },
                })),
                mergeMap((ret) => {
                    const nextActObs = of(
                        rootSignOffApprovalResult(
                            {
                                ...act.payload,
                                approval: ret,
                                goalsCollection: {
                                    commitmentKpis: ret.commitment.goals,
                                },
                            },
                            act.storePath,
                        ),
                    );
                    return concat(nextActObs);
                }),
                catchError((err) => {
                    console.log(err);
                    return of(null);
                }),
            );
        }),
    );

export default [
    rootReload,
    rootSave,
    rootRetrieveCommitmentDiff,
    rootSendApproval,
    rootDelete,
    rootRetrieveObservationSummary,
    rootSignOffApproval,
];
